import Typography from '@mui/material/Typography';
import React from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import { useMutation } from '@tanstack/react-query';
import { checkAccess, postRequestAccess } from '../../api/mobileapi';
import { AccessPointData, PermissionData } from '../../api/types';
import { Locale } from '../../translations/translations';
import { AdamsError, getErrorMessage } from '../../utils/errors';
import { AccessPointCard } from '../AccessPointCard/AccessPointCard';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { Severity, useToast } from '../ToastProvider/ToastProvider';
import { RequestAccessForm } from './RequestAccessForm';
import { useNavigate } from "react-router-dom";

//TODO refactor that later
const refetchData = ({ access, language }: { access: AccessPointData; language: Locale }) => {
    if (access) {
        checkAccess({ language, accessPointCode: access.accessPointCode });
    }
};

interface RequestAccessProps {
    accessInformation: AccessPointData;
    permissionInfo: PermissionData;
    translations: Record<string, string>;
}

export const RequestAccess: React.FC<RequestAccessProps> = ({ accessInformation, permissionInfo, translations }) => {
    const toast = useToast();
    const { currentLanguage } = useHomeData();
    const navigate = useNavigate();

    const {
        mutate: requestAccess,
        isSuccess,
        isLoading: isSubmitting,
        error,
    } = useMutation(postRequestAccess, {
        onSuccess: () => {
            refetchData({ access: accessInformation, language: currentLanguage });
            if (permissionInfo.requestExists) {
                toast(translations.ACRQ_REQUEST_EXISTS, Severity.info);
            } else {
                toast(translations.ACRQ_REQUEST_CREATED, Severity.success);
            }
            navigate(`/accesspoint/${accessInformation.accessPointCode}/permissions/${permissionInfo.permissionCode}`);
        },
        onError: (err: AdamsError) => {
            const message = getErrorMessage(err);
            if (message) toast(message, Severity.error);
        },
    });

    return (
        <AccessPointCard
            accessInformation={accessInformation}
            backButtonTo={`/accesspoint/${accessInformation.accessPointCode}/permissions/${permissionInfo.permissionCode}`}
        >
            <>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    {permissionInfo.requestCanBeCreated
                        ? `${translations.ACRQ_TITLE} ${permissionInfo.permissionCode}`
                        : translations.PERMISSIONS_MISSING_ACCESS_REQUEST_UNAVAILABLE}
                </Typography>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {permissionInfo.permissionDescription}
                </Typography>

                {error && (
                    <Typography color="error" gutterBottom>
                        {getErrorMessage(error)}
                    </Typography>
                )}

                {isSubmitting && <CircularProgress />}

                {isSuccess && (
                    <Typography color="primary" gutterBottom>
                        {translations.ACRQ_REQUEST_CREATED}
                    </Typography>
                )}

                {!isSuccess && permissionInfo.requestExists && (
                    <Typography color="primary" gutterBottom>
                        {translations.ACRQ_REQUEST_EXISTS}
                    </Typography>
                )}

                {!isSuccess && !isSubmitting && !permissionInfo.requestExists && permissionInfo.requestCanBeCreated && (
                    <RequestAccessForm
                        accessInformation={accessInformation}
                        currentLanguage={currentLanguage}
                        permissionInfo={permissionInfo}
                        requestAccess={requestAccess}
                        translations={translations}
                    />
                )}
            </>
        </AccessPointCard>
    );
};
